import { clientApi } from "@/utils/api";
import {
  TGetQueryTalentListReqDto,
  TGetTalentListReqDto,
  TGetTalentListResDto,
  TTalentListOutputDto,
} from "@/api/services/main/talent/dtos/get-talent-list.dto";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { TDeleteTalentResDto } from "@/api/services/main/talent/dtos/delete-talent.dto";
import { TCreateTalentReqDto, TCreateTalentResDto } from "@/api/services/main/talent/dtos/create-talent-req.dto";
import { useParams } from "react-router-dom";
import { TGetTalentResDto } from "@/api/services/main/talent/dtos/get-talent.dto";
import { TUpdateTalentReqDto, TUpdateTalentResDto } from "@/api/services/main/talent/dtos/update-talent-req.dto";
import { TCreateTalentCommentReqDto, TCreateTalentCommentResDto } from "@/api/services/main/talent/dtos/create-talent-comment.dto";
import { TGetTalentCommentsResDto } from "@/api/services/main/talent/dtos/get-talent-comment.dto";
import { TGetTalentExistsResDto } from "@/api/services/main/talent/dtos/get-talent-exists.dto";
import { TCheckPassportDuplicateResDto } from "@/api/services/main/talent/dtos/check-passport-duplicate.dto";
import { createUrlWithParams } from "@/utils/url";
import { TTalentRowExcel } from "@/features/talent/types/talentExcel";
import { TExportTalentResDto } from "@/api/services/main/talent/dtos/export-talent.dto";
import { useMemo } from "react";

const processDataBeforeCallApi = (payload: TCreateTalentReqDto) => {
  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return formData.append(key, "");
    if (Array.isArray(value) && value.length === 0) return formData.append(key, "");

    if (value instanceof File) {
      formData.append(key, value);
    } else if (Array.isArray(value)) {
      if (value.every((v) => v instanceof File)) {
        value.forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, JSON.stringify(value));
      }
    } else if (typeof value === "number") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
};

export const getTalentListApi = async (params: TGetTalentListReqDto): Promise<TGetTalentListResDto> => {
  const { data } = await clientApi.get<TGetTalentListResDto>("/talents", { params });
  return data;
};

export const useTalentList = (params: TGetTalentListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_LIST, params],
    queryFn: () => getTalentListApi(params),
  });
};

export const useTalentOptions = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_LIST],
    queryFn: () => getTalentListApi({}),
  });
  const options = useMemo(
    () =>
      data
        ? data.data.map((item) => ({
            fullName: item.fullName,
            birthday: item.birthday,
          }))
        : [],
    [data],
  );
  return {
    options,
    ...rest,
  };
};

export const deleteTalentApi = async (ids: number[], isAll = false, filter: TGetQueryTalentListReqDto = {}): Promise<TDeleteTalentResDto> => {
  const { data } = await clientApi.delete<TDeleteTalentResDto>("/talents", {
    data: {
      talentIds: ids || [],
      isAll,
      ...filter,
    },
  });
  return data;
};

export const createTalentApi = async (payload: TCreateTalentReqDto): Promise<TCreateTalentResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.post<TCreateTalentResDto>("/talents", formData);
  return rs.data;
};

export const fetchTalentInfoApi = async (id: string | number): Promise<TGetTalentResDto> => {
  const { data } = await clientApi.get<TGetTalentResDto>(`/talents/${id}`);
  return data;
};

export const useTalentInfo = (id?: number) => {
  const { talentId } = useParams();

  const fetchId = id ?? talentId;

  const isValidId = useMemo(() => {
    if (!fetchId) return false;
    const idNumber = Number(fetchId);
    return !isNaN(idNumber) && Number.isInteger(idNumber) && idNumber > 0;
  }, [fetchId]);

  return useQuery({
    queryKey: [QUERY_KEYS.TALENT.FETCH_DETAIL, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchTalentInfoApi(fetchId) : undefined;
    },
    enabled: isValidId,
  });
};

export const updateTalentApi = async (id: string | number, payload: TUpdateTalentReqDto): Promise<TUpdateTalentResDto> => {
  const formData = processDataBeforeCallApi(payload);
  const rs = await clientApi.put<TUpdateTalentResDto>(`/talents/${id}`, formData);
  return rs.data;
};

export const createTalentCommentApi = async (talentId: number, payload: TCreateTalentCommentReqDto): Promise<TCreateTalentCommentResDto> => {
  const rs = await clientApi.post<TCreateTalentCommentResDto>(`/talents/${talentId}/comments`, payload);
  return rs.data;
};

export const fetchTalentCommentsApi = async (id: string | number): Promise<TGetTalentCommentsResDto> => {
  const { data } = await clientApi.get<TGetTalentCommentsResDto>(`/talents/${id}/comments`);
  return data;
};

export const useTalentCommentsList = (id?: number) => {
  const { talentId } = useParams();

  const fetchId = id ?? talentId;
  return useQuery({
    queryKey: [QUERY_KEYS.TALENT_COMMENT.FETCH_LIST, fetchId],
    queryFn: async () => {
      return fetchId ? await fetchTalentCommentsApi(fetchId) : undefined;
    },
    enabled: Boolean(fetchId),
  });
};

export const fetchTalentExistsApi = async (id: number, fullName: string, birthday?: string | null): Promise<TGetTalentExistsResDto> => {
  const params = { fullName, ...(birthday && { birthday }), ...(id && { id }) };
  const url = createUrlWithParams("/talents/exists", params);

  const { data } = await clientApi.get<TGetTalentExistsResDto>(url);

  return data;
};

export const fetchCheckPassportDuplicateApi = async (id: number | undefined, passportNumber: string): Promise<TCheckPassportDuplicateResDto> => {
  const params = { passportNumber, ...(id && { id }) };
  const url = createUrlWithParams("/talents/check-passport-duplicate", params);

  const { data } = await clientApi.get<TCheckPassportDuplicateResDto>(url);

  return data;
};

export const validateImportTalentApi = async (payload: TTalentRowExcel[]) => {
  const rs = await clientApi.post("/talents/validate-import", payload);
  return rs.data;
};

export const importTalentsApi = async (payload: TTalentListOutputDto[]) => {
  const rs = await clientApi.post("/talents/import", payload);
  return rs.data;
};

export const exportTalentApi = async (ids: number[], isAll = false, filter: TGetQueryTalentListReqDto = {}) => {
  const { data } = await clientApi.get<TExportTalentResDto>("/talents/export", {
    params: {
      talentIds: ids || [],
      isAll: Number(isAll),
      ...filter,
    },
  });
  return data;
};
